import React, { useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { useQuery } from "react-query";
import { useOkapiKy } from "@folio/stripes/core";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { RecekTextField } from "../inputs";
import { fetchAgencyPublisherById } from "../../api/publisherService";
import { agencyPublisherQueryKey } from "../../hooks/title/useResolvePublisher";
import { filterImprints, getOwnerImprints, } from "../../utils/imprintSuggestions";
import styles from "./TitleImprintField.module.css";
var FIELD_ID = "title-imprint";
var LISTBOX_ID = "".concat(FIELD_ID, "-suggestions");
var optionId = function (index) { return "".concat(LISTBOX_ID, "-").concat(index); };
/**
 * Resolves the imprint names of the title owner held in form state. The owner
 * is normally the full agency publisher (with its public alternative names);
 * while it is only a bare `{ id }` (right after a selection), the detail is
 * fetched through the shared agency-publisher query cache.
 */
var useOwnerImprints = function () {
    var okapiKy = useOkapiKy();
    var owner = useWatch({ name: "owner" });
    var ownerId = owner === null || owner === void 0 ? void 0 : owner.id;
    var needsDetail = !!ownerId && !(owner === null || owner === void 0 ? void 0 : owner.name);
    var ownerDetail = useQuery(agencyPublisherQueryKey(ownerId !== null && ownerId !== void 0 ? ownerId : ""), function () { return fetchAgencyPublisherById(okapiKy, ownerId); }, { enabled: needsDetail }).data;
    return useMemo(function () { return getOwnerImprints(needsDetail ? ownerDetail : owner); }, [needsDetail, ownerDetail, owner]);
};
/**
 * Free-text input with a suggestion list (ARIA combobox). Suggestions are
 * only an offer: any typed text is kept as the value.
 */
var ImprintInput = function (_a) {
    var value = _a.value, onChange = _a.onChange, suggestions = _a.suggestions, error = _a.error, inputRef = _a.inputRef;
    var formatMessage = useIntl().formatMessage;
    var _b = useState(false), isOpen = _b[0], setIsOpen = _b[1];
    var _c = useState(-1), highlightedIndex = _c[0], setHighlightedIndex = _c[1];
    var visibleSuggestions = isOpen ? filterImprints(suggestions, value) : [];
    var isExpanded = visibleSuggestions.length > 0;
    var open = function () {
        setIsOpen(true);
        setHighlightedIndex(-1);
    };
    var close = function () {
        setIsOpen(false);
        setHighlightedIndex(-1);
    };
    var select = function (suggestion) {
        onChange(suggestion);
        close();
    };
    var handleKeyDown = function (event) {
        if (event.key === "ArrowDown" || event.key === "ArrowUp") {
            if (!isOpen) {
                open();
                event.preventDefault();
                return;
            }
            if (!isExpanded)
                return;
            event.preventDefault();
            var step_1 = event.key === "ArrowDown" ? 1 : -1;
            var count_1 = visibleSuggestions.length;
            setHighlightedIndex(function (current) {
                return current < 0 && step_1 < 0 ? count_1 - 1 : (current + step_1 + count_1) % count_1;
            });
        }
        else if (event.key === "Enter") {
            if (isExpanded && highlightedIndex >= 0) {
                // Only swallow Enter while picking a suggestion, so the form still submits otherwise.
                event.preventDefault();
                select(visibleSuggestions[highlightedIndex]);
            }
        }
        else if (event.key === "Escape") {
            if (isExpanded)
                event.preventDefault();
            close();
        }
    };
    return (React.createElement("div", { className: styles.combobox, onBlur: close },
        React.createElement(RecekTextField, { ref: inputRef, id: FIELD_ID, name: "imprint", labelId: "ui-br-agency.title.imprint.field.label", value: value, error: error, role: "combobox", "aria-autocomplete": "list", "aria-expanded": isExpanded, "aria-controls": LISTBOX_ID, "aria-activedescendant": isExpanded && highlightedIndex >= 0
                ? optionId(highlightedIndex)
                : undefined, onFocus: open, onKeyDown: handleKeyDown, onChange: function (event) {
                onChange(event.target.value);
                open();
            } }),
        isExpanded && (React.createElement("ul", { id: LISTBOX_ID, role: "listbox", className: styles.listbox, "aria-label": formatMessage({
                id: "ui-br-agency.title.imprint.suggestions.label",
            }) }, visibleSuggestions.map(function (suggestion, index) { return (React.createElement("li", { key: suggestion, id: optionId(index), role: "option", "aria-selected": index === highlightedIndex, className: index === highlightedIndex
                ? "".concat(styles.option, " ").concat(styles.highlighted)
                : styles.option, 
            // mousedown (not click) so the input does not blur and close the list first
            onMouseDown: function (event) {
                event.preventDefault();
                select(suggestion);
            }, onMouseEnter: function () { return setHighlightedIndex(index); } }, suggestion)); })))));
};
/**
 * Imprint ("Značka (imprint)") field of the title form. Suggests the IMPRINT
 * alternative names of the current title owner, switches them when the owner
 * changes (the entered value is kept) and still accepts any free text.
 */
export var TitleImprintField = function () {
    var control = useFormContext().control;
    var suggestions = useOwnerImprints();
    return (React.createElement(Controller, { control: control, name: "imprint", render: function (_a) {
            var _b, _c;
            var field = _a.field, fieldState = _a.fieldState;
            return (React.createElement(ImprintInput, { value: (_b = field.value) !== null && _b !== void 0 ? _b : "", onChange: field.onChange, suggestions: suggestions, error: (_c = fieldState.error) === null || _c === void 0 ? void 0 : _c.message, inputRef: field.ref }));
        } }));
};
